import React, { useEffect, useState, MutableRefObject, useRef } from "react";
import "video.js/dist/video-js.css";
import style from "../style.module.scss";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import classnames from "classnames";
import { convertSecondsToTimeString } from "@jmc/utils/utils/date-to-string";
import "videojs-youtube/dist/Youtube.js";
import { useVideoJS } from "react-hook-videojs";
import { MediaStatus } from "../MediaStatus";
import isEqual from "lodash/isEqual";
import { YoutubeInfo } from "./YoutubeInfo";
import { CookieConsent } from "@jmc/solid-design-system/src/utils/cookieConsent";

export interface YouTubeProps {
    /** The Youtube video id */
    videoId: string;
    /** Current Playing Video ref*/
    playVideoRef?: MutableRefObject<any>;
    /** A title that is shown when the video is not playing yet or paused */
    title?: string;
    /** A summary that is shown when the video is not playing yet or paused */
    summary?: string;
    /** Youtube account id */
    accountId?: string;
    /** Youtube application id */
    applicationId?: string;
    /** Youtube player id */
    playerId?: string;
    /** Should the player be playable? */
    videoPlayable?: boolean;
    /** Controls the offset of the video in seconds */
    offset?: number;
    /** A Background image that is shown when the video is not playing yet */
    thumbnail?: string;
    /** Should the video play automatically */
    autoplay?: boolean;
    /** Command to toggle playing mode */
    play?: boolean;
    /** a callback triggered every 15-250ms while video is playing. See https://docs.Youtube.com/Youtube-player/1.x/Player.html#progress-event */
    onTimeUpdate?: () => void;
    /** What happens when the video starts playing */
    onPlay?: (videoref: MutableRefObject<any> | null) => void;
    /** Should video have an overlay when not playing? */
    hasOverlay?: boolean;
    /** Alignment of summary and title */
    contentAlignment?: "left" | "center" | "right";
    contentVerticalAlignment?: "top" | "center" | "bottom";
    /** Should the video player controls be visible? */
    showControls?: boolean;
    /** Should the big play button be visible? */
    showLargePlayButton?: boolean;
    /** Should the duration be shown when the video is loaded? */
    showDuration?: boolean;
    /** Check if its audio or not */
    isAudio?: boolean;
    /** Display a warning if the video cannot be displayed because the cookies are not accepted */
    warningMessage?: JSX.Element;
    /** Check if videoplayer is shown in header ex. to set title to h2 */
    showInHeader?: boolean;
    /** Check time when paused and send to Parent element Cardimpression */
    mediastop?: (status: MediaStatus, currentTime: number | string) => void;
    /** Get total length time */
    getLength?: (duration: number) => void;
    /** Get cookie acceptance (Optanon) status */
    cookieAccept?: (accepted: boolean) => void;
    /** Get current time of player */
    getCurrentTime?: (currentTime: number) => void;
    /** SEO title */
    seoTitle?: string;
    /** Alt tag */
    alt?: string;
    imageFocus?: ImagePosition;
    className?: string;
    /** Set to true when coming from the preloader to start playing immediately */
    startPlaying?: boolean;
}

export const Youtube: React.FunctionComponent<YouTubeProps> = ({
    videoId,
    playVideoRef,
    title,
    summary,
    videoPlayable = true,
    offset = 0,
    thumbnail,
    autoplay = false,
    play = false,
    onTimeUpdate,
    onPlay,
    hasOverlay = false,
    contentAlignment = "center",
    contentVerticalAlignment = "center",
    showControls = true,
    showLargePlayButton = true,
    showDuration = false,
    isAudio = false,
    warningMessage = undefined,
    showInHeader = false,
    mediastop = null,
    getLength = null,
    cookieAccept = null,
    getCurrentTime = null,
    imageFocus = null,
    className = null,
    startPlaying = false,
}: YouTubeProps) => {
    const { Video, player, ready } = useVideoJS({
        techOrder: ["youtube"],
        sources: [{ type: "video/youtube", src: `https://www.youtube.com/watch?v=${videoId}` }],
        youtube: { enablePrivacyEnhancedMode: true },
    });

    if (ready) {
        // Do something with the video.js player object.
    }

    const [playing, setPlaying] = React.useState(play);
    const [playerApi, setPlayerApi] = React.useState(null);
    const [, setDuration] = React.useState("");
    const [, setDurationIsVisible] = React.useState(showDuration);
    const [accepted, setAccepted] = useState("loading");
    const videoref = useRef(null);

    useEffect(() => {
        if (!playerApi) setPlayerApi(player);
    }, [ready]);

    useEffect(() => {
        if (startPlaying && playerApi) {
            setTimeout(() => {
                playerApi.play();
            }, 400);
        }
    }, [playerApi]);

    React.useEffect(() => {
        if (!playerApi) return;
        const poster = playerApi.posterImage;

        if (imageFocus) {
            const el = poster.el();
            el.style.backgroundPosition = imageFocus.replace("-", " ");
        }

        playerApi.on("posterchange", () => {
            if (hasOverlay) {
                const el = poster.el();
                const bg = el.style.backgroundImage;
                el.style.backgroundImage = `linear-gradient(0deg, rgba(20, 20, 20, 0.38), rgba(20, 20, 20, 0.38)), ${bg}`;
            }
        });

        playerApi.on("play", () => {
            if (!playing) {
                mediastop && mediastop(MediaStatus.PLAY, playerApi.cache_.currentTime);
                setPlaying(true);
                showDuration && setDurationIsVisible(false);
                onPlay && onPlay(videoref);
            }
        });
        playerApi.on("pause", () => {
            if (playing) {
                mediastop && mediastop(MediaStatus.PAUSE, playerApi.cache_.currentTime);
                showDuration && setDurationIsVisible(true);
                setPlaying(false);
            }
        });
        playerApi.on("timeupdate", () => {
            getCurrentTime && getCurrentTime(playerApi.cache_.currentTime);
            onTimeUpdate && onTimeUpdate();
        });
        playerApi.on("ready", () => {
            autoplay && playerApi.muted(true);
            autoplay && playerApi.autoplay(true);
            autoplay && mediastop && mediastop(MediaStatus.PLAY, playerApi.cache_.currentTime);
        });
        playerApi.on("loadedmetadata", () => {
            const rawDuration = convertSecondsToTimeString(playerApi.cache_.duration);
            setDuration(rawDuration.replace(/^0/, ""));
            getLength && getLength(playerApi.cache_.duration);
        });
        playerApi.on("touchstart", () => {
            if (showControls) {
                return;
            }

            if (playerApi.paused() && playerApi.hasStarted()) {
                playerApi.play();
            } else {
                playerApi.pause();
            }
        });
    }, [playerApi, playing]);

    React.useEffect(() => {
        if (!playerApi) return;
        playerApi?.currentTime(offset);
    }, [playerApi, offset]);

    React.useEffect(() => {
        playerApi && playerApi.play();
    }, [offset]);

    React.useEffect(() => {
        if (!playerApi) return;
        play ? playerApi?.play() : playerApi?.pause();
    }, [playerApi, play]);

    useEffect(() => {
        const scriptStatus = (): void => {
            const activeGroups = (window as any)?.OptanonActiveGroups as string;
            const currentStatus =
                activeGroups?.split(",")?.includes(`${CookieConsent.FUNTIONAL_COOKIES}`) || activeGroups === undefined
                    ? "ready"
                    : "blocked";
            setAccepted(currentStatus);
            cookieAccept && cookieAccept(currentStatus === "ready");
        };

        scriptStatus();
        window.addEventListener("OptanonActiveGroupsChanged", scriptStatus);
        return () => {
            window.removeEventListener("OptanonActiveGroupsChanged", scriptStatus);
        };
    });

    // Pause all other player based on video reference
    React.useEffect(() => {
        if (!playVideoRef) return;
        if (playerApi && !isEqual(playVideoRef, videoref) && playing) {
            playerApi.pause();
        }
    }, [playVideoRef, playerApi]);

    return (
        <div
            className={classnames(
                className,
                videoPlayable ? style.player : style.disabled,
                !isAudio || thumbnail ? style.videoPlayer : style.ajs,
                !showLargePlayButton || (!thumbnail && isAudio) ? style.hidePlay : style.playButton,
                accepted === "blocked" && style.blocked,
            )}
            ref={videoref}
        >
            {accepted === "blocked" && warningMessage && (
                <div id={"superduperid"} className={style.warning} data-test-id={`VideoPlayer.${videoId}`}>
                    {warningMessage}
                </div>
            )}

            <div style={{ display: accepted === "ready" ? "block" : "none" }} data-test-id={`VideoPlayer.${videoId}`}>
                <Video
                    className={classnames("vjs-big-play-centered", "vjs-16-9", "video-js")}
                    controls
                    controlBar={showControls}
                    autoPlay={autoplay}
                    poster={thumbnail ? thumbnail : ""}
                />
            </div>

            {playing === false && (
                <YoutubeInfo
                    title={title}
                    showInHeader={showInHeader}
                    summary={summary}
                    isAudio={isAudio}
                    thumbnail={thumbnail}
                    contentAlignment={contentAlignment}
                    contentVerticalAlignment={contentVerticalAlignment}
                    showLargePlayButton={showLargePlayButton}
                />
            )}
        </div>
    );
};

export default Youtube;
